import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID, RESPONSE_INIT } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { ApiService, LyricSeo } from '@lyricstream/shared';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { buildLyricSeo } from '../services/lyric-seo';
import { SeoService } from '../services/seo.service';

/** Au-delà, le rendu serveur n'attend plus l'API : la page part avec les balises par défaut. */
const SSR_FETCH_TIMEOUT_MS = 4000;

/**
 * Pose les balises SEO d'un chant DANS LE HTML SERVEUR (titre, description, canonical, og:*, twitter:*).
 *
 * Volontairement limité au serveur et aux balises : il n'alimente PAS `LyricService.currentLyric`, sinon le
 * serveur rendrait le contenu du chant alors que le navigateur démarre vide (écart d'hydratation). Côté
 * navigateur, c'est `LyricReaderComponent` qui met les balises à jour avec le chant chargé.
 * Appelle `/seo` et NON `/api/lyrics/:id` : ce dernier compte une vue à chaque appel (le rendu serveur et les
 * robots gonfleraient les compteurs). Ne bloque jamais la navigation : toute erreur ou lenteur de l'API laisse la page se rendre normalement.
 */
export const lyricSeoResolver: ResolveFn<boolean> = async (route) => {
  if (!isPlatformServer(inject(PLATFORM_ID))) return true;

  const api = inject(ApiService);
  const seo = inject(SeoService);
  const responseInit = inject(RESPONSE_INIT, { optional: true });

  const id = route.paramMap.get('id');
  const lang = route.pathFromRoot.map((r) => r.paramMap.get('lang')).find(Boolean) ?? 'fr';
  if (!id) return true;

  const result = await firstValueFrom(
    api.get<LyricSeo>(`/api/lyrics/${encodeURIComponent(id)}/seo`).pipe(
      timeout(SSR_FETCH_TIMEOUT_MS),
      catchError((err: unknown) => of(err instanceof HttpErrorResponse ? err : new HttpErrorResponse({ status: 0 }))),
    ),
  );

  if (result instanceof HttpErrorResponse) {
    // Chant absent ou non public : vrai 404 pour les robots, et jamais indexé. Autre erreur (API en panne,
    // délai dépassé) : on ne dit rien de faux, les balises par défaut restent.
    if (result.status === 404 || result.status === 403) {
      if (responseInit) responseInit.status = 404;
      seo.set({ title: 'LyricStream', noindex: true, lang });
    }
    return true;
  }

  seo.set(buildLyricSeo(result, lang));
  return true;
};
